import { useState, useRef, useEffect } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { SearchBarProps } from '@/types/blog';

const SearchBar = ({ 
  onSearch, 
  placeholder = "Tìm kiếm bài viết...", 
  loading = false,
  defaultValue = '',
  className = ''
}: SearchBarProps & { className?: string }) => {
  const [query, setQuery] = useState(defaultValue);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Update query when defaultValue changes
  useEffect(() => {
    setQuery(defaultValue);
  }, [defaultValue]);

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim().length >= 2) {
      onSearch(query.trim());
    }
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    
    // Auto-search on empty query to clear results
    if (value === '') {
      onSearch('');
    }
  };

  // Clear search
  const clearSearch = () => {
    setQuery('');
    onSearch('');
    inputRef.current?.focus();
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Clear on Escape
    if (e.key === 'Escape') {
      clearSearch();
    }
  };

  return (
    <div className={`relative ${className}`}>
      <form onSubmit={handleSubmit} className="relative">
        {/* Search icon */}
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon 
            className={`h-5 w-5 transition-colors ${
              isFocused ? 'text-m2-primary' : 'text-m2-outline'
            }`} 
          />
        </div>

        {/* Search input */}
        <input
          ref={inputRef}
          type="search"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          className={`block w-full pl-10 pr-12 py-3 border border-eh-border rounded-eh text-sm text-m2-on-surface placeholder:text-eh-muted focus:outline-none focus:ring-2 focus:ring-eh-forest-ink/30 focus:border-eh-forest-ink transition-colors ${
            loading ? 'bg-m2-surface-low' : 'bg-m2-surface-lowest hover:border-eh-forest-ink/50'
          }`}
          disabled={loading}
          autoComplete="off"
          spellCheck="false"
        />

        {/* Clear button */}
        {query && (
          <button
            type="button"
            onClick={clearSearch}
            className="absolute inset-y-0 right-8 flex items-center pr-1 text-m2-outline hover:text-m2-on-surface-variant transition-colors"
            disabled={loading}
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}

        {/* Search button / Loading spinner */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          {loading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-m2-primary border-t-transparent"></div>
          ) : (
            <button
              type="submit"
              disabled={query.trim().length < 2}
              className={`p-1 rounded transition-colors ${
                query.trim().length >= 2
                  ? 'text-m2-primary hover:text-m2-primary'
                  : 'text-m2-outline-variant cursor-not-allowed'
              }`}
            >
              <MagnifyingGlassIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </form>

      {/* Search suggestions (placeholder for future enhancement) */}
      {isFocused && query.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-m2-surface-lowest border border-eh-border rounded-eh shadow-eh-soft z-50 max-h-60 overflow-y-auto">
          {query.length < 2 ? (
            <div className="p-3 text-sm text-m2-on-surface-variant text-center">
              Nhập ít nhất 2 ký tự để tìm kiếm
            </div>
          ) : (
            <div className="p-3 text-sm text-m2-on-surface-variant text-center">
              Nhấn Enter để tìm kiếm "{query}"
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;